import React from "react";
import {
  Grid,
  Segment,
  Header,
  Icon,
  Table,
  Progress,
  Label,
  Loader,
  Tab,
} from "semantic-ui-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { StatCard } from "../../StatCard";
import "./AttendanceTab.scss";

export function AttendanceTab(props) {
  const { attendanceData } = props;

  if (!attendanceData) {
    return <Loader active>Cargando datos de asistencia...</Loader>;
  }

  const period = attendanceData.period || {};
  const today = attendanceData.today || {};

  return (
    <Tab.Pane>
      <Grid stackable>
        {/* Stats Cards */}
        <Grid.Row columns={4}>
          <Grid.Column>
            <StatCard
              title="Asistencia del Período"
              value={`${parseFloat(period.attendanceRate || 0).toFixed(1)}%`}
              icon="chart line"
              color="#21ba45"
            />
          </Grid.Column>
          <Grid.Column>
            <StatCard
              title="Asistencia Hoy"
              value={`${parseFloat(today.attendanceRate || 0).toFixed(1)}%`}
              icon="calendar check"
              color="#2185d0"
            />
          </Grid.Column>
          <Grid.Column>
            <StatCard
              title="Tiempo Prom. Tardanza"
              value={`${parseFloat(
                attendanceData.tardiness?.avgLateMinutes || 0
              ).toFixed(0)} min`}
              icon="clock"
              color="#f2c037"
            />
          </Grid.Column>
          <Grid.Column>
            <StatCard
              title="Estudiantes en Riesgo"
              value={attendanceData.studentsAtRisk?.count || 0}
              icon="warning sign"
              color="#db2828"
            />
          </Grid.Column>
        </Grid.Row>

        {/* Charts Row */}
        <Grid.Row columns={2}>
          {/* Attendance Distribution Pie Chart */}
          <Grid.Column width={8}>
            <Segment>
              <Header as="h4">
                <Icon name="chart pie" />
                Distribución de Asistencia (Período)
              </Header>
              {period.totalRecords > 0 ? (
                <>
                  <div className="attendance-distribution-summary">
                    <div className="distribution-item present">
                      <span className="value">{period.presentCount || 0}</span>
                      <span className="label">Presentes</span>
                    </div>
                    <div className="distribution-item absent">
                      <span className="value">{period.absentCount || 0}</span>
                      <span className="label">Ausentes</span>
                    </div>
                    <div className="distribution-item late">
                      <span className="value">
                        {attendanceData.tardiness?.tardinessCount || 0}
                      </span>
                      <span className="label">Tardanzas</span>
                    </div>
                  </div>
                  {(() => {
                    const attendancePieData = [
                      {
                        name: "Presentes",
                        value: period.presentCount || 0,
                        fill: "#10b981",
                      },
                      {
                        name: "Ausentes",
                        value: period.absentCount || 0,
                        fill: "#ef4444",
                      },
                      {
                        name: "Tardanzas",
                        value: attendanceData.tardiness?.tardinessCount || 0,
                        fill: "#f59e0b",
                      },
                    ].filter((item) => item.value > 0);

                    return attendancePieData.length > 0 ? (
                      <div
                        style={{
                          width: "100%",
                          height: 200,
                          marginTop: "1rem",
                        }}
                      >
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={attendancePieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {attendancePieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : null;
                  })()}
                </>
              ) : (
                <p>No hay datos disponibles</p>
              )}
            </Segment>
          </Grid.Column>

          {/* Attendance by Grade */}
          <Grid.Column width={8}>
            <Segment>
              <Header as="h4">
                <Icon name="users" />
                Asistencia por Grado
              </Header>
              {attendanceData.byGrade?.length > 0 ? (
                <div className="attendance-by-grade">
                  {attendanceData.byGrade.map((grade) => {
                    const rate = parseFloat(grade.attendanceRate || 0);
                    return (
                      <div
                        key={grade.gradeId}
                        className="grade-attendance-item"
                      >
                        <div className="grade-info">
                          <span className="grade-name">{grade.gradeName}</span>
                          <span
                            className="grade-rate"
                            style={{
                              color:
                                rate >= 85
                                  ? "#10b981"
                                  : rate >= 75
                                  ? "#f59e0b"
                                  : "#ef4444",
                            }}
                          >
                            {rate.toFixed(1)}%
                          </span>
                        </div>
                        <Progress
                          percent={rate}
                          size="small"
                          color={
                            rate >= 85 ? "green" : rate >= 75 ? "yellow" : "red"
                          }
                        />
                        <div className="grade-details">
                          <span className="detail present">
                            <Icon name="check" size="small" /> {grade.present}
                          </span>
                          <span className="detail absent">
                            <Icon name="times" size="small" /> {grade.absent}
                          </span>
                          <span className="detail late">
                            <Icon name="clock" size="small" /> {grade.tardanza}
                          </span>
                          <span className="detail justified">
                            <Icon name="file" size="small" />{" "}
                            {grade.justificado}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>No hay datos por grado disponibles</p>
              )}
            </Segment>
          </Grid.Column>
        </Grid.Row>

        {/* Students at Risk Table */}
        {attendanceData.studentsAtRisk?.students?.length > 0 && (
          <Grid.Row>
            <Grid.Column>
              <Segment>
                <Header as="h4">
                  <Icon name="warning sign" color="red" />
                  Estudiantes con Baja Asistencia (
                  {attendanceData.studentsAtRisk.count})
                </Header>
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Estudiante</Table.HeaderCell>
                      <Table.HeaderCell>Grado</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">
                        % Asistencia
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">
                        Presente / Total
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {attendanceData.studentsAtRisk.students
                      .slice(0, 10)
                      .map((student, index) => {
                        const rate = parseFloat(student.attendanceRate || 0);
                        return (
                          <Table.Row key={student.studentId || index}>
                            <Table.Cell>{student.studentName}</Table.Cell>
                            <Table.Cell>{student.gradeName}</Table.Cell>
                            <Table.Cell textAlign="center">
                              <Label
                                color={
                                  rate < 75
                                    ? "red"
                                    : rate < 80
                                    ? "orange"
                                    : "yellow"
                                }
                              >
                                {rate.toFixed(1)}%
                              </Label>
                            </Table.Cell>
                            <Table.Cell textAlign="center">
                              {student.presentDays || 0} /{" "}
                              {student.totalDays || 0}
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                  </Table.Body>
                </Table>
              </Segment>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Tab.Pane>
  );
}
